import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ExternalLink, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, TOKEN_KEY } from "@/lib/api";
import { Logo } from "@/components/site/Logo";
import { ContentEditor } from "@/components/admin/ContentEditor";
import { TicketManager } from "@/components/admin/TicketManager";
import { AnalyticsDashboard } from "@/components/admin/AnalyticsDashboard";
import { MemberManager } from "@/components/admin/MemberManager";

export default function Admin() {
  const nav = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => {
      localStorage.removeItem(TOKEN_KEY);
      nav("/admin/login");
    });
  }, [nav]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    nav("/admin/login");
  };

  if (!user) return <div className="flex min-h-screen items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div className="min-h-screen bg-[#F3F7FF]" data-testid="admin-panel-container">
      <header className="glass sticky top-0 z-40 border-b border-slate-200">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 lg:px-8">
          <Logo testId="admin-logo" small />
          <div className="flex items-center gap-3">
            <a href="/" target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 text-sm font-semibold text-slate-700 hover:text-blue-600 sm:flex" data-testid="admin-view-site"><ExternalLink className="h-4 w-4" /> View site</a>
            <button onClick={logout} data-testid="admin-logout-button" className="flex items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:border-red-400 hover:text-red-600"><LogOut className="h-4 w-4" /> Log out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 lg:px-8">
        <h1 className="font-display text-2xl font-extrabold text-slate-900 sm:text-3xl">Admin dashboard</h1>
        <p className="mt-1 text-sm text-slate-600">Signed in as {user.email}</p>
        <Tabs defaultValue="analytics" className="mt-8">
          <TabsList className="h-auto rounded-full border border-slate-200 bg-white p-1">
            <TabsTrigger value="analytics" data-testid="admin-tab-analytics" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white">Visitor analytics</TabsTrigger>
            <TabsTrigger value="members" data-testid="admin-tab-members" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white">Members</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="admin-tab-tickets" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white">Support tickets</TabsTrigger>
            <TabsTrigger value="content" data-testid="admin-tab-content" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-blue-600 data-[state=active]:text-white">Homepage & contact</TabsTrigger>
          </TabsList>
          <TabsContent value="analytics" className="mt-8"><AnalyticsDashboard /></TabsContent>
          <TabsContent value="members" className="mt-8"><MemberManager /></TabsContent>
          <TabsContent value="tickets" className="mt-8"><TicketManager /></TabsContent>
          <TabsContent value="content" className="mt-8"><ContentEditor /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
